import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Cache to store already checked password hashes to prevent duplicate API calls
// Key is the SHA-1 hash, value is whether it was pwned
const pwnedPasswordCache = new Map<string, boolean>();

@Injectable({
  providedIn: 'root',
})
export class PwnedPasswordService {
  /**
   * Checks if a password has been compromised using the Have I Been Pwned API.
   * Uses k-anonymity to protect the password - only sends first 5 chars of SHA-1 hash.
   * Implements caching to prevent duplicate API calls.
   *
   * @param password - The password to check
   * @returns Observable<boolean> - true if password has been pwned, false otherwise
   */
  checkPassword(password: string): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.performPasswordCheck(password, observer);
    }).pipe(
      catchError(() => {
        console.warn('Have I Been Pwned API check failed');
        return of(false); // Don't block user if API fails
      }),
    );
  }

  /**
   * Performs the password breach check by hashing, checking cache, and calling API if needed.
   * Separated for better testability and readability.
   *
   * @param password - The password to check
   * @param observer - The observer to notify with results
   */
  private performPasswordCheck(password: string, observer: any): void {
    this.hashPassword(password).then((hash) => {
      // Check cache using hash instead of plaintext password
      if (pwnedPasswordCache.has(hash)) {
        const isPwned = pwnedPasswordCache.get(hash) ?? false;
        observer.next(isPwned);
        observer.complete();
        return;
      }

      const prefix = hash.substring(0, 5);
      const suffix = hash.substring(5);

      fetch(`https://api.pwnedpasswords.com/range/${prefix}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error('API request failed');
          }
          return response.text();
        })
        .then((text) => {
          // Stream through response without allocating full array
          const found = this.findHashInResponse(text, suffix);

          // Cache the result using hash as key
          pwnedPasswordCache.set(hash, found);

          observer.next(found);
          observer.complete();
        })
        .catch((error) => {
          console.error('Error checking password:', error);
          observer.error(error);
        });
    });
  }

  /**
   * Searches for a hash suffix in the API response without allocating a full array.
   * Processes the text line-by-line for better memory efficiency.
   *
   * @param text - The API response text
   * @param targetSuffix - The hash suffix to search for
   * @returns true if the hash suffix is found, false otherwise
   */
  private findHashInResponse(text: string, targetSuffix: string): boolean {
    const upperTargetSuffix = targetSuffix.toUpperCase();
    let startIndex = 0;

    // Process line by line without creating array of all lines
    while (startIndex < text.length) {
      // Find next line break
      let endIndex = text.indexOf('\r\n', startIndex);
      if (endIndex === -1) {
        endIndex = text.length;
      }

      // Extract line and check if it matches
      const line = text.substring(startIndex, endIndex);
      const colonIndex = line.indexOf(':');

      if (colonIndex !== -1) {
        const hashSuffix = line.substring(0, colonIndex);
        if (hashSuffix.toUpperCase() === upperTargetSuffix) {
          return true;
        }
      }

      // Move to next line
      startIndex = endIndex + 2; // Skip \r\n
    }

    return false;
  }

  /**
   * Hashes a password using SHA-1 algorithm via Web Crypto API.
   */
  private async hashPassword(password: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex.toUpperCase();
  }

  /**
   * Clears the password cache (useful for testing or memory management)
   */
  clearCache(): void {
    pwnedPasswordCache.clear();
  }
}
